import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, next, once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { APIData, DSSetup, RequestError, Service } from 'webapp-types';
import {
	ForecastComparisonPeriod,
	ForecastMethod,
	ForecastStatusType,
	MetricDetailsSettingsTab,
	ModelName,
	TimeframeType,
	TimeUnit,
	Unit,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { equal, isAlive, normalizeFilters, serializeMetricSetupData } from 'webapp/utils/common';
import { hashCode } from 'webapp/utils/number';

import didUpdate from 'webapp/modifiers/did-update';
import toggleScrolledClass from 'webapp/modifiers/toggle-scrolled-class';
import ForecastsDataConfidenceScoreComponent from 'webapp/components/forecasts/data/confidence-score';
import type { PreviewData } from 'webapp/components/forecasts/data/index';
import ForecastsDataComponent from 'webapp/components/forecasts/data/index';
import ForecastsHeaderActionsComponent from 'webapp/components/forecasts/header-actions';
import MetricDetailsForecastsTopbarComponent from 'webapp/components/metric-details/forecasts/topbar';
import MetricDetailsRelatedForecastsComponent from 'webapp/components/metric-details/related-forecasts';
import MetricDetailsSettingsTabsComponent from 'webapp/components/metric-details/settings-tabs';
import MetricDetailsSettingsForecastMethodologyComponent from 'webapp/components/metric-details/settings/forecast-methodology';
import MetricDetailsSettingsVisualizeComponent from 'webapp/components/metric-details/settings/visualize';

import type { ForecastRecord } from 'webapp/models';
import type { Forecast } from 'webapp/models-custom';
import type { ForecastMethodology } from 'webapp/models-custom/forecast';
import { EXPIRE_INFO } from 'webapp/models-custom/infolayer-item/base';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type { RegressorMetricData } from 'webapp/models-custom/regressor-metric';
import RegressorMetric from 'webapp/models-custom/regressor-metric';

import { asyncReplyEventName } from 'webapp/services/event-manager';
import type { InfolayerItem } from 'webapp/services/infolayer';
import type { ForecastTabState, SharedMetricDetailsState } from 'webapp/services/metric-details';
import { ForecastPusherEvents } from 'webapp/services/model-manager/forecasts';
import type { RSjqXHR, RSPromise } from 'webapp/services/request';

interface RequestState {
	isLoading: boolean;
	error?: Error | RequestError; // a RequestError when the request failed
	xhr?: RSjqXHR;
}

export class TrackedRequestState {
	@tracked isLoading = false;
	@tracked error?: Error | RequestError; // a RequestError when the request failed
	@tracked xhr?: RSjqXHR;

	constructor(initialState?: Partial<RequestState>) {
		if (initialState) {
			this.isLoading = initialState.isLoading ?? false;
			this.error = initialState.error;
			this.xhr = initialState.xhr;
		}
	}
}

interface Args {
	model: Forecast | null;
	onSaveForecast: (forecast: Forecast) => Promise<void>;
	onDeleteForecast: (forecast: Forecast) => Promise<void>;
	// shared metric setup state (passed from MetricExplorer parent)
	sharedState: SharedMetricDetailsState;
	initialTabState?: ForecastTabState | null;
	onShare?: () => void;
	onClose?: () => void;
	onSharedStateChange?: (data: SharedMetricDetailsState) => void;
	onTabStateChange?: (state: ForecastTabState) => void;
	onDisabledTooltipTextChange?: (text: string | undefined) => void;
}

interface MetricDetailsForecastsExplorerComponentInterface {
	Args: Args;
	Blocks: { header: [] };
}

const DEFAULT_ALLOWED_SEASONALITY = [TimeUnit.WEEK, TimeUnit.MONTH, TimeUnit.YEAR];
const GENERIC_ERROR = new Error('Failed to load forecast data');

export default class MetricDetailsForecastsExplorerComponent extends Component<MetricDetailsForecastsExplorerComponentInterface> {
	@service('store') declare storeService: Service.Store;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('popup') declare popupService: Service.Popup;
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('router') declare routerService: Service.Router;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;

	@tracked metricSetup!: MetricSetup; // Initialized in constructor or reconstructForecastFromPendingData
	@tracked limitations!: MetricLimitations; // Initialized in constructor or reconstructForecastFromPendingData
	@tracked forecast: Forecast | null = null; // Forecast model (always duplicated for safe editing)

	// track the original state to determine UI behavior
	@tracked private originalForecastWasSaved = false;
	@tracked originalForecast: Forecast | null = null; // original model with ID for property editing

	@tracked allowedSeasonality = DEFAULT_ALLOWED_SEASONALITY;

	// currently shown data, can be from snapshot
	@tracked previewData: PreviewData | null = null;
	@tracked confidenceScore: APIData.Forecast.ConfidenceScore | null = null;

	// Snapshots
	@tracked snapshots: APIData.Forecast.Snapshot[] = [];
	@tracked isLoadingSnapshots = false;
	@tracked currentShownSnapshotId: number | null = null;

	// STATUS
	@tracked isFirstDayOfTheWeekError = false;

	// Request state management
	@tracked mainForecastState = new TrackedRequestState({ isLoading: false });
	@tracked isAddOrRemoveInProgress = false;

	// Settings tabs
	settingsTabs: MetricDetailsSettingsTab[] = [
		MetricDetailsSettingsTab.VISUALIZE,
		MetricDetailsSettingsTab.METHODOLOGY,
	];

	// Async request tracking
	@tracked latestMainForecastRequestId: string | null = null;
	private loadingInfoLayerTimeoutId: number | null = null;
	private loadingInfoLayerItem: InfolayerItem | null = null;

	// LOADING MESSAGE
	loadingMessages: string[] = [
		'Fetching historical data',
		'Preparing data for forecasting',
		'Crunching the numbers',
		'Running calculations',
		'Analyzing trends',
		'Building the forecast model',
		'Fine-tuning the model',
		"Testing the model's accuracy",
		'Adjusting for seasonal variations',
		'Incorporating external factors',
		'Generating the forecast',
		'Validating the forecast',
		'Finalizing the forecast',
		'Formatting the data for display',
		'Almost there, just a few more seconds',
	];
	@tracked loadingMessageIntervalTime = (60 * 1000) / this.loadingMessages.length; // 60 seconds is forecast request timeout, Tracked so it will invoke restartTypewriter

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// check if user clicked notification to load a pending forecast
		if (this.loadPendingForecastFromQueryParams(args)) {
			return; // pending forecast loaded, skip normal initialization
		}

		if (args.model) {
			// existing forecast model
			this.initializeFromSavedForecast(args.model);
		} else {
			// no existing forecast model — check if we should load a saved forecast by its ID
			const forecastId = this.args.initialTabState?.forecastId;
			if (forecastId) {
				const savedForecast = this.mmForecastsService.forecastModels.find(
					(f) => f.id === forecastId,
				);
				if (savedForecast) {
					this.initializeFromSavedForecast(savedForecast);
					return;
				}
			}

			// initialize from shared metric setup
			this.initializeFromMetricSetupData(this.args.sharedState);
		}
	}

	private initializeFromSavedForecast(model: Forecast): void {
		// always duplicate the forecast model when entering the explorer
		this.initializeFromModel(model);

		// load snapshots from the original model (which has an ID) if it's a saved forecast
		if (!model.isNew) {
			next(this, () => {
				void this.loadSnapshots(model);
			});
		}

		const metricSetup =
			this.forecast?.metricSetup ||
			new MetricSetup({
				key: '',
				spaceAccessId: null,
				dimensions: [],
				filters: null,
			});

		this.applyMetricSetup(metricSetup);

		void this.limitations.limitationsLoaded?.then(() => {
			const snapshotIdToLoad = this.forecast?.lastSnapshotId;

			if (snapshotIdToLoad && this.originalForecastWasSaved) {
				this.currentShownSnapshotId = snapshotIdToLoad;
				this.requestForecastSnapshotData(snapshotIdToLoad);
			} else {
				// for new forecasts or duplicated forecasts (no ID), request fresh forecast data
				this.requestForecastDataOnce();
			}
		});
	}

	private initializeFromMetricSetupData(data: SharedMetricDetailsState) {
		// create a fresh forecast (not saved)
		this.forecast = null;
		this.originalForecast = null;
		this.originalForecastWasSaved = false;
		this.previewData = null;
		this.confidenceScore = null;
		this.currentShownSnapshotId = null;
		this.snapshots = [];
		this.isLoadingSnapshots = false;

		const metricSetup = new MetricSetup(data.metricSetup);

		this.applyMetricSetup(metricSetup);

		if (metricSetup.key) {
			this.createUnsavedForecast(metricSetup);

			// apply initial forecast settings from parent if provided (e.g., from shared link or tab switch)
			const forecast = this.forecast as Forecast | null;
			if (this.args.initialTabState && forecast) {
				const settings = this.args.initialTabState;
				if (settings.methodology) forecast.methodology = settings.methodology;
				if (settings.seasonality) forecast.seasonality = settings.seasonality;
				if (settings.timeframe !== undefined) forecast.timeframe = settings.timeframe;
				if (settings.endDate !== undefined) forecast.endDate = settings.endDate;
				if (settings.granulation) forecast.granulation = settings.granulation;
				if (settings.comparisonPeriod)
					forecast.comparisonPeriod = settings.comparisonPeriod;
				if (settings.holidaysCountry !== undefined)
					forecast.holidaysCountry = settings.holidaysCountry;
			}

			// save pending requestId BEFORE reportForecastStateToParent clears it.
			const pendingRequestId = this.args.initialTabState?.requestId ?? null;

			this.reportForecastStateToParent();
			this.reportSharedStateToParent();

			void this.limitations.limitationsLoaded?.then(() => {
				if (!this.tryReusePendingPreviewRequest(pendingRequestId)) {
					this.requestForecastDataOnce();
				}
			});
		}

		this.reportMetricSelectorStateToParent();
	}

	get metricSetupData() {
		return this.metricSetup.metricSetupData;
	}

	get isError(): boolean {
		return !!this.mainForecastState.error || !!this.forecast?.hasStatusError;
	}

	get errorButtonText(): string {
		return this.isFirstDayOfTheWeekError ? 'Change settings' : 'Try again';
	}

	get showConfidenceScore() {
		return !!this.confidenceScore && !this.isError && !this.isMainForecastLoading;
	}

	get isForecastSaved(): boolean {
		return !!this.forecast && this.originalForecastWasSaved;
	}

	get relatedForecastsTitle(): string {
		const name = this.metricSetup?.metric?.name || this.metricSetup?.key || '';
		return `Other saved forecast scenarios for ${name}`;
	}

	// GRANULAR LOADING STATE GETTERS
	get isMainForecastLoading(): boolean {
		return this.mainForecastState.isLoading || !!this.forecast?.isGenerating;
	}

	get isInitialLoading(): boolean {
		// show skeletons if loading AND (no data yet OR was in error state)
		return this.isMainForecastLoading && (!this.previewData || this.isError);
	}

	get isRefreshingWithData(): boolean {
		// show opacity overlay if loading AND has existing data AND not in error
		return this.isMainForecastLoading && !!this.previewData && !this.isError;
	}

	get isViewingHistoricalSnapshot(): boolean {
		// User is viewing a historical (non-latest) snapshot
		return (
			this.currentShownSnapshotId !== null &&
			this.forecast?.lastSnapshotId !== null &&
			this.currentShownSnapshotId !== this.forecast?.lastSnapshotId
		);
	}

	get areSettingsDisabled(): boolean {
		return this.isForecastSaved || this.isViewingHistoricalSnapshot;
	}

	get isRecalculateDisabled(): boolean {
		return this.isViewingHistoricalSnapshot || this.isMainForecastLoading;
	}

	@action
	changeMethodology(methodology: ForecastMethodology): void {
		if (this.forecast) {
			this.forecast.methodology = methodology;
			this.changeForecastSettings();
		}
	}

	@action
	changeParameters(seasonality: TimeUnit[], holidaysCountry: string): void {
		if (this.forecast) {
			this.forecast.seasonality = seasonality;
			this.forecast.holidaysCountry = holidaysCountry || null;
			this.changeForecastSettings();
		}
	}

	@action
	onParentMetricChange(): void {
		const newMetricSetup = new MetricSetup(this.args.sharedState.metricSetup);

		if (!this.metricSetup.isMatchingMetricSetup(newMetricSetup)) {
			this.initializeFromMetricSetupData(this.args.sharedState);
			debounce(this, this.trackMetricChanged, 300);
		}
	}

	trackMetricChanged(): void {
		if (this.metricSetup.metric) {
			this.metricDetailsService.trackMetricChanged(this.metricSetup.metric, 'forecast');
		}
	}

	@action
	changeForecastSettings(): void {
		// clear last requested payload to force a fresh request
		// we no longer make separate requests for regressors
		this.lastRequestedPreviewPayload = {};
		this.reportForecastStateToParent();
		this.requestForecastData();
	}

	createUnsavedForecast(metricSetup: MetricSetup): void {
		const forecastRecord = this.mmForecastsService.createNewRecord({
			// setup parameters (will be locked after first save)
			granulation: TimeUnit.MONTH,
			comparisonPeriod: ForecastComparisonPeriod.PREVIOUS_PERIOD,

			// metric setup data
			metricSetupKey: metricSetup.key || '',
			metricSetupSpaceAccessId: metricSetup.spaceAccessId,
			metricSetupDimensions: metricSetup.dimensions,
			metricSetupFilters: metricSetup.filtersData,

			// additional parameters (changeable after save)
			timeframe: {
				type: TimeframeType.Y,
				year: new Date().getFullYear(),
			},
			methodology: {
				type: ForecastMethod.DATABOX,
				extra: null,
			},
			seasonality: [],
			holidaysCountry: null,
		});

		// create BufferedModel wrapper using the model manager for consistency
		this.forecast = this.mmForecastsService.createModelFromRecord(forecastRecord);
	}

	setOptimalUnit() {
		const metricSetup = this.metricSetup;

		if (!metricSetup.metricData) {
			return;
		}

		if (
			!metricSetup.hasUnits ||
			(metricSetup.selectedUnit && metricSetup.selectedUnit !== Unit.ALL)
		) {
			return;
		}

		const key = this.metricSetup.key,
			prevUnit = this.metricSetup.unitName;

		if (!key) {
			return;
		}

		void this.mmMetricsService
			.getOptimalUnit(metricSetup.metricData)
			.then((unit) => {
				// check if user has not changed the metrickey inbetween
				if (key !== this.metricSetup.key) {
					return;
				}

				if (prevUnit !== this.metricSetup.unitName) {
					return;
				}

				this.metricSetup.unitName = unit;
			})
			.catch();
	}

	private lastRequestedPreviewPayload: Partial<APIData.Forecast.PreviewRequest> = {}; // empty until the first request

	@action
	requestForecastData(recalculate = false, retry = false): void {
		if (!this.forecast) {
			return;
		}

		// check if main forecast methodology is LINEAR_CHANGE without a value
		if (this.forecast.methodology?.type === ForecastMethod.LINEAR_CHANGE) {
			const changeValue = this.forecast.methodology?.extra?.changeValue;
			if (changeValue === null || changeValue === undefined) {
				// don't send preview request without required linear change value
				return;
			}
		}

		// build request payload using new format with forecast model data
		const forecast: APIData.Forecast.PreviewRequest['forecast'] = {
			timeframe: this.forecast.timeframe,
			endDate: this.forecast.endDate,
			granulation: this.forecast.granulation,
			metricSetup: serializeMetricSetupData(this.metricSetupData)!,

			seasonality: this.forecast.seasonality,
			holidaysCountry: this.forecast.holidaysCountry,
			methodology: this.forecast.methodology,
			regressors: this.forecast.buildRegressorsPayload(),
			comparisonPeriod: this.forecast.comparisonPeriod,
		};

		const previewPayload = { forecast, recalculate };

		const oldHash = hashCode(JSON.stringify(this.lastRequestedPreviewPayload)),
			newHash = hashCode(JSON.stringify(previewPayload));

		if (!recalculate && !retry && oldHash === newHash) {
			// same params, skip preview
			return;
		}

		this.cancelActivePreviewRequest();

		this.lastRequestedPreviewPayload = previewPayload;

		// clear error on successful new request so we can show correct loading state (skeleton vs opacity)
		this.mainForecastState.isLoading = true;
		this.mainForecastState.xhr = undefined;
		this.isFirstDayOfTheWeekError = false;

		// clear previous status objects before new request
		if (this.forecast) {
			this.forecast.statusObject = null;
		}

		// determine if this is a saved forecast on latest snapshot
		const isSavedForecast = this.originalForecastWasSaved;
		const isOnLatestSnapshot =
			this.currentShownSnapshotId === null ||
			this.currentShownSnapshotId === this.forecast.lastSnapshotId;

		if (isSavedForecast && isOnLatestSnapshot) {
			// call PUT: create new snapshot
			this.requestForecastUpdateData({ forecast });
		} else {
			// call preview endpoint
			this.requestUnsavedForecastPreview(previewPayload);
		}
	}

	@action
	requestForecastSnapshotData(snapshotId: number): void {
		this.cancelActivePreviewRequest();

		// Set main forecast loading state for snapshot requests
		this.mainForecastState.isLoading = true;
		this.mainForecastState.error = undefined;
		this.mainForecastState.xhr = undefined;

		const promise = this.requestService.api<APIData.Forecast.SnapshotPreviewResponse>(
			'GET',
			`/forecasts/snapshots/${snapshotId}`,
			undefined,
			{ timeout: 120 * 1000 },
		);

		this.mainForecastState.xhr = promise.jqPromise as RSjqXHR;

		void (promise
			.then((reply: APIData.Forecast.SnapshotPreviewResponse) => {
				if (reply) {
					if (reply.forecast) {
						// use the forecast serializer to normalize the API data
						const forecastSerializer = this.storeService.serializerFor(
							ModelName.FORECAST,
						);
						const normalizedPayload = forecastSerializer.normalize(
							this.storeService.modelFor(ModelName.FORECAST),
							reply.forecast,
							ModelName.FORECAST,
						);

						const forecastData = normalizedPayload.data.attributes;

						// create a temporary record (not persisted to store) for viewing the snapshot
						const tempRecord = this.storeService.createRecord(
							ModelName.FORECAST,
							forecastData,
						) as ForecastRecord;
						this.forecast = this.mmForecastsService.createModelFromRecord(tempRecord);

						// update metricSetup to match the snapshot forecast's data
						this.metricSetup = new MetricSetup({
							key: this.forecast.metricSetupKey,
							spaceAccessId: this.forecast.metricSetupSpaceAccessId,
							dimensions: this.forecast.metricSetupDimensions,
							filters: this.forecast.metricSetupFilters,
						});

						if (this.forecast.hasStatusError) {
							const errorMessage = this.forecast.statusObjectMessage || '';
							this.mainForecastState.error = new Error(errorMessage);
						}

						// handle regressors data if present
						if (reply.regressors && reply.regressors.length > 0) {
							const regressorMetrics = reply.regressors.map((regressorItem) => {
								// Use the regressor-metric serializer to normalize the API data
								const regressorSerializer =
									this.storeService.serializerFor('regressor-metric');

								const normalizedRegressorPayload = regressorSerializer.normalize(
									this.storeService.modelFor('regressor-metric'),
									regressorItem.regressor,
									'regressor-metric',
								);

								const regressorData = {
									...(normalizedRegressorPayload.data
										.attributes as RegressorMetricData),
									forecastId: this.args.model?.id?.toString() || undefined,
								};

								const regressor = new RegressorMetric(regressorData);

								regressor.setStatusFromResponse(
									regressorItem.regressor?.statusObject || null,
								);

								// only initialize table block if no error status
								if (!regressor.hasStatusError) {
									regressor.previewData = {
										dataSelects: regressorItem.dataSelects,
										sample: regressorItem.sample,
										monthsOfUsedHistoricalData:
											reply.monthsOfUsedHistoricalData || 0,
									};

									regressor.initializeTableBlock();
								}

								return regressor;
							});

							this.forecast.regressors = regressorMetrics;
						}
					}

					this.confidenceScore = reply.confidenceScore || null;

					this.previewData = Object.assign(this.previewData || {}, {
						snapshotId: snapshotId,
						dataSelects: reply.dataSelects,
						sample: reply.sample,
						monthsOfUsedHistoricalData: reply.monthsOfUsedHistoricalData,
						allowedSeasonality: reply.allowedSeasonality,
					});
				} else {
					this.mainForecastState.error = GENERIC_ERROR;
				}
			})
			.catch((errorResponse: RequestError) => {
				this.requestService.showAllValidationErrors(errorResponse);
				if (errorResponse.obj!.statusText !== 'abort') {
					this.mainForecastState.error = errorResponse;
				}
			})
			.finally(() => {
				this.mainForecastState.isLoading = false;
				this.reportMetricSelectorStateToParent();
			}) as RSPromise<void>);

		this.mainForecastState.xhr = promise.jqPromise as RSjqXHR;
	}

	@action
	changeMetricSetupData(newValue: DSSetup.MetricSetupData): void {
		this.metricSetup.setFromMetricSetupData(newValue);

		this.requestForecastDataOnce();
	}

	@action
	executeErrorButtonAction(): void {
		if (this.isFirstDayOfTheWeekError) {
			this.aiChatService.onNavigateToCompany();
		} else {
			this.requestForecastData(false, true);
		}
	}

	@action
	selectSnapshot(snapshotId: number) {
		this.currentShownSnapshotId = snapshotId;
		this.requestForecastSnapshotData(snapshotId);
	}

	@action
	requestForecastDataOnce() {
		once(this, this.requestForecastData, false);
	}

	@action
	handleRegressorChange(): void {
		if (this.forecast) {
			// regressors are now bundled in the main forecast request
			// just refresh the main forecast which will include all regressor data
			this.handleRequestForecastPreview();
		}
	}

	@action
	handleRequestForecastPreview(): void {
		// called by data component when we need to refresh forecast with current regressors
		// clear the last requested payload to force a fresh request
		this.lastRequestedPreviewPayload = {};
		this.requestForecastData(false, false);
	}

	@action
	recalculateForecast(): void {
		if (!this.forecast) {
			return;
		}

		this.forecast.updateTimeframeToCurrent();

		this.forecast.trackEvent(TrackingEvent.FORECAST_RECALCULATE);

		// request forecast data with recalculate flag set to true
		this.requestForecastData(true);
	}

	@action
	duplicateForecast(): void {
		if (!this.forecast || this.accountService.handleDemo()) {
			return;
		}

		// store references to data that needs to be preserved to avoid refetching
		const tableBlockRef = this.forecast.tableBlock;
		const previewDataRef = this.previewData;
		const regressorsWithPreviewData = this.forecast.regressors;

		const copyData = this.forecast.toJSON();

		// remove regressors from copyData to avoid serialization issues
		// we'll set them separately to preserve RegressorMetric instances
		delete copyData.regressors;

		// create new unsaved record with the copied data
		const newRecord = this.storeService.createRecord(
			ModelName.FORECAST,
			copyData as Record<string, unknown>,
		) as ForecastRecord;
		const duplicatedForecast = this.mmForecastsService.createModelFromRecord(newRecord);

		// update timeframe to current values for the duplicated forecast
		duplicatedForecast.updateTimeframeToCurrent();

		// restore regressors with their preview data as RegressorMetric instances
		// this bypasses serialization and preserves all regressor data and methods
		duplicatedForecast.regressors = regressorsWithPreviewData;

		// restore table block reference
		if (tableBlockRef) {
			duplicatedForecast.tableBlock = tableBlockRef;
		}

		this.forecast = duplicatedForecast;
		this.originalForecastWasSaved = false;

		this.metricSetup = duplicatedForecast.metricSetup;
		this.previewData = previewDataRef;

		// reset snapshot-related state since this is now a new unsaved forecast
		this.currentShownSnapshotId = null;
		this.snapshots = [];

		this.reportForecastStateToParent();
	}

	@action
	deleteForecast(): void {
		if (this.forecast && this.args.onDeleteForecast) {
			this.cancelActivePreviewRequest();
			void this.args.onDeleteForecast(this.forecast);
		}
	}

	@action
	addToMyForecasts() {
		if (this.isAddOrRemoveInProgress || this.accountService.handleDemo()) {
			return;
		}
		this.isAddOrRemoveInProgress = true;

		const model = this.forecast!;

		model.title = model.generateDefaultTitle();

		model.trackEvent(TrackingEvent.FORECAST_SAVED);

		this.args
			.onSaveForecast(model)
			.then(() => {
				// In route mode, @model is the original forecast from the route.
				// In tabbed mode (@model is null), use the saved forecast directly.
				const savedModel =
					this.args.model && !this.args.model.isNew ? this.args.model : model;

				this.originalForecast = savedModel;
				this.originalForecastWasSaved = true;
				this.reportForecastStateToParent();

				next(this, () => {
					if (this.forecast?.lastSnapshotId) {
						this.currentShownSnapshotId = this.forecast.lastSnapshotId;
					}

					if (savedModel && !savedModel.isNew) {
						void this.loadSnapshots(savedModel);
					}
				});
			})
			.catch(() => {
				// save failed — keep originalForecastWasSaved as false so
				// the UI continues showing the save button instead of the title
			})
			.finally(() => {
				this.isAddOrRemoveInProgress = false;
			});
	}

	private initializeFromModel(model: Forecast | null) {
		if (model) {
			// always duplicate the model for safe editing (prevents snapshot overwrites)
			this.forecast = this.mmForecastsService.duplicateForecast(model);

			this.originalForecastWasSaved = !model.isNew;
			this.currentShownSnapshotId = model.lastSnapshotId;

			// keep reference to original model with ID for property editing (e.g., title)
			if (!model.isNew) {
				this.originalForecast = model;
			} else {
				this.originalForecast = null;
			}
		}

		// reset component state when switching from saved to unsaved forecast
		if (!model) {
			this.forecast = null;
			this.originalForecast = null;
			this.originalForecastWasSaved = false;
			this.previewData = null;
			this.confidenceScore = null;
			this.currentShownSnapshotId = null;
			this.snapshots = [];
			this.isLoadingSnapshots = false;
		}

		this.reportMetricSelectorStateToParent();
	}

	private cancelActivePreviewRequest() {
		// Cancel main forecast request
		if (this.mainForecastState.xhr && this.mainForecastState.xhr.readyState !== 4) {
			this.mainForecastState.xhr.abort();
			this.mainForecastState.isLoading = false;

			this.loadingMessageIntervalTime = this.loadingMessageIntervalTime; // restartTypewriter
		}

		// unregister previous request's event listener to prevent handling outdated responses
		if (this.latestMainForecastRequestId) {
			this.mmForecastsService.unregisterNotification(this.latestMainForecastRequestId);
			this.clearLoadingInfoLayer();
		}
	}

	private updateSeasonalityFromAllowed(allowedSeasonality: TimeUnit[]) {
		this.allowedSeasonality = allowedSeasonality;
		// we need to update last requested preview also, so we don't request it again on params update
		const requestedForecast = this.lastRequestedPreviewPayload.forecast,
			requestedSeasonality: TimeUnit[] = requestedForecast?.seasonality || [];
		const validRequestedSeasonality = requestedSeasonality.filter((s) =>
			allowedSeasonality.includes(s),
		);

		if (requestedForecast && !equal(requestedSeasonality, validRequestedSeasonality)) {
			requestedForecast.seasonality = validRequestedSeasonality;

			if (this.forecast) {
				this.forecast.seasonality = validRequestedSeasonality;
			}
		}
	}

	private resubscribeToExistingRequest(requestId: string): void {
		this.latestMainForecastRequestId = requestId;
		this.mainForecastState.isLoading = true;
		this.mainForecastState.error = undefined;

		// re-report state so the requestId persists in parent's ForecastTabState
		// for future tab switches
		this.reportForecastStateToParent();

		// set forecast to generating state to show proper loading UI
		if (this.forecast) {
			this.forecast.statusObject = {
				timestamp: new Date().getTime(),
				type: ForecastStatusType.SNAPSHOT_GENERATING,
			};
		}

		// subscribe to the same bgEvent that the original request is waiting for.
		const eventName = asyncReplyEventName(
			ForecastPusherEvents.FORECAST_PREVIEW_DATA_COMPLETE,
			requestId,
		);

		this.eventManagerService.on(eventName, this, (asyncReply) => {
			// the forecast preview reply carries the normalized forecast data
			const eventData = asyncReply as APIData.Forecast.NormalizedForecastEventData;

			// ignore if a newer request has superseded this one
			if (eventData.requestId !== this.latestMainForecastRequestId) {
				this.mmForecastsService.unregisterNotification(eventData.requestId);
				return true; // unsubscribe
			}

			// if user navigated away entirely, store for notification
			if (this.handleNavigationAway(eventData, 'Forecast generation failed')) {
				return true; // unsubscribe
			}

			this.applyPreviewResponseData(eventData);
			return true; // done, unsubscribe
		});
	}

	// check if there's a pending preview request from a previous tab visit
	// requestId must be captured BEFORE reportForecastStateToParent() runs
	// because that call overwrites the parent's stored requestId with null.
	// true -> a pending request was found (either stored data consumed or re-subscribed).
	// false -> no pending request — caller should proceed with a new request.
	private tryReusePendingPreviewRequest(pendingRequestId: string | null): boolean {
		if (!pendingRequestId) {
			return false;
		}

		// check if the response already arrived while we were away
		const storedData = this.mmForecastsService.getPendingForecast(pendingRequestId);
		if (storedData) {
			this.latestMainForecastRequestId = pendingRequestId;
			this.applyPreviewResponseData(storedData);
			return true;
		}

		// check if the request is still in-flight (notification still registered)
		if (this.mmForecastsService.shouldShowNotification(pendingRequestId)) {
			this.resubscribeToExistingRequest(pendingRequestId);
			return true;
		}

		// request already completed or was cancelled — proceed with a new request
		return false;
	}

	private initializeAsyncForecastRequest(): string {
		const requestId = this.requestService.generateRequestId();
		this.latestMainForecastRequestId = requestId;
		this.mmForecastsService.registerForNotification(requestId);
		this.showLoadingInfoLayer();

		// report requestId to parent so it persists in ForecastTabState across tab switches
		this.reportForecastStateToParent();

		return requestId;
	}

	private processStatusObject(eventData: APIData.Forecast.NormalizedForecastEventData): void {
		if (eventData.forecast.config.statusObject && this.forecast) {
			this.forecast.statusObject = eventData.forecast.config.statusObject;
			const errorMessage = this.forecast.statusObjectMessage || '';
			if (this.forecast.hasStatusError) {
				this.mainForecastState.error = new Error(errorMessage);
			}
		}
	}

	private applyPreviewResponseData(
		eventData: APIData.Forecast.NormalizedForecastEventData,
	): void {
		this.clearLoadingInfoLayer();
		this.processStatusObject(eventData);

		if (eventData.success && eventData.previewData) {
			this.applyPreviewSuccessData(eventData.previewData);
		} else if (!eventData.success && !eventData.forecast?.config?.statusObject) {
			this.mainForecastState.error = GENERIC_ERROR;
		}

		this.mainForecastState.isLoading = false;
		this.reportMetricSelectorStateToParent();
		this.mmForecastsService.unregisterNotification(eventData.requestId);
	}

	private applyPreviewSuccessData(
		previewData: NonNullable<APIData.Forecast.NormalizedForecastEventData['previewData']>,
	): void {
		if (this.forecast?.hasStatusError) {
			return;
		}

		this.confidenceScore = previewData.confidenceScore;
		this.previewData = {
			dataSelects: previewData.dataSelects,
			sample: previewData.sample,
			monthsOfUsedHistoricalData: previewData.monthsOfUsedHistoricalData,
		};

		this.mainForecastState.error = undefined;
		this.isFirstDayOfTheWeekError = false;
		if (this.forecast) {
			this.forecast.statusObject = null;
		}
		this.updateSeasonalityFromAllowed(
			previewData.allowedSeasonality || DEFAULT_ALLOWED_SEASONALITY,
		);
		this.updateRegressorsFromResponse(previewData);
	}

	private handleNavigationAway(
		eventData: APIData.Forecast.NormalizedForecastEventData,
		defaultErrorMessage: string,
	): boolean {
		// component destroyed (e.g. user switched from forecast to explore tab) or user navigated away entirely
		if (!isAlive(this) || !this.metricDetailsService.isActivatedRouteExplorer) {
			if (eventData.success) {
				// store event data so socket.ts can show notification toast
				this.mmForecastsService.storeForecastData(eventData.requestId, eventData);
			} else {
				const statusObject = eventData.forecast.config.statusObject;
				const errorMessage =
					statusObject?.data?.message || statusObject?.data?.type || defaultErrorMessage;

				this.infolayerService.error(errorMessage);
				this.mmForecastsService.unregisterNotification(eventData.requestId);
			}
			return true; // unsubscribe - let socket.ts handle notification
		}
		return false; // continue processing
	}

	private handleAsyncRequestError(
		errorResponse: RequestError,
		requestId: string,
		errorType:
			| ForecastStatusType.PREVIEW_GENERATION_FAILED
			| ForecastStatusType.SNAPSHOT_GENERATION_FAILED,
		errorMessage: string,
	): void {
		this.requestService.showAllValidationErrors(errorResponse);
		if (errorResponse.obj && errorResponse.obj.statusText !== 'abort') {
			if (this.forecast) {
				this.mmForecastsService.trackForecastMetricEvent(
					TrackingEvent.FORECASTING_ERROR,
					this.forecast,
					'forecasting',
				);

				// Set appropriate error statusObject on forecast
				this.forecast.statusObject = {
					timestamp: new Date().getTime(),
					type: errorType,
					data: {
						message: errorMessage,
					},
				};
			}

			const validationErrorMessage = errorResponse.errors[0]?.message || '';
			this.isFirstDayOfTheWeekError =
				validationErrorMessage.includes('first day of the week');
			this.mainForecastState.error = errorResponse;
		}
		this.mainForecastState.isLoading = false;
		this.mmForecastsService.unregisterNotification(requestId);
	}

	private executeAsyncForecastRequest(config: {
		method: 'POST' | 'PUT';
		endpoint: string;
		payload: APIData.Forecast.PreviewRequest;
		navigationAwayErrorMessage: string;
		errorStatusType:
			| ForecastStatusType.PREVIEW_GENERATION_FAILED
			| ForecastStatusType.SNAPSHOT_GENERATION_FAILED;
		errorMessage: string;
		onSuccess: (eventData: APIData.Forecast.NormalizedForecastEventData) => boolean;
	}): void {
		const requestId = this.initializeAsyncForecastRequest();

		const promise = this.requestService.apiWithAsyncReply(
			config.method,
			config.endpoint,
			config.payload,
			{ requestId, timeout: 120 * 1000 },
			(rawEventData) => {
				const eventData = rawEventData as APIData.Forecast.NormalizedForecastEventData;
				// ignore outdated responses - only process the latest request
				if (eventData.requestId !== this.latestMainForecastRequestId) {
					this.mmForecastsService.unregisterNotification(eventData.requestId);
					return true; // unsubscribe from outdated event
				}

				// check if user navigated away
				if (this.handleNavigationAway(eventData, config.navigationAwayErrorMessage)) {
					return true; // unsubscribe
				}

				this.clearLoadingInfoLayer();

				// process status object if present
				this.processStatusObject(eventData);

				// handle success or error with custom logic
				// onSuccess returns true if it handled loading state, false otherwise
				let loadingStateHandled = false;
				if (eventData.success) {
					loadingStateHandled = config.onSuccess(eventData);
				} else if (!eventData.forecast.config.statusObject) {
					this.mainForecastState.error = GENERIC_ERROR;
				}

				// only set loading state if onSuccess didn't handle it
				if (!loadingStateHandled) {
					this.mainForecastState.isLoading = false;
					this.reportMetricSelectorStateToParent();
				}

				this.mmForecastsService.unregisterNotification(eventData.requestId);
				return true; // done, unsubscribe
			},
		);

		promise.catch((errorResponse: RequestError) => {
			this.handleAsyncRequestError(
				errorResponse,
				requestId,
				config.errorStatusType,
				config.errorMessage,
			);
		});
	}

	private requestUnsavedForecastPreview(previewPayload: APIData.Forecast.PreviewRequest): void {
		// Set forecast to generating state before request
		if (this.forecast) {
			this.forecast.statusObject = {
				timestamp: new Date().getTime(),
				type: ForecastStatusType.SNAPSHOT_GENERATING,
			};
		}

		this.executeAsyncForecastRequest({
			method: 'POST',
			endpoint: '/forecasts/preview',
			payload: previewPayload,
			navigationAwayErrorMessage: 'Forecast generation failed',
			errorStatusType: ForecastStatusType.PREVIEW_GENERATION_FAILED,
			errorMessage: 'Forecast preview generation failed',
			onSuccess: (eventData) => {
				if (eventData.previewData) {
					this.applyPreviewSuccessData(eventData.previewData);
				}
				return false; // let generic method handle loading state
			},
		});
	}

	private requestForecastUpdateData(forecastPayload: APIData.Forecast.PreviewRequest): void {
		// use originalForecast (authoritative saved reference in both route and tabbed modes);
		// args.model is null in tabbed/drawer mode so reading id from it yields an empty URL segment
		const originalForecast = this.originalForecast;
		const forecastId = originalForecast?.id?.toString() || '';

		if (!forecastId) {
			this.mainForecastState.isLoading = false;
			return;
		}

		// Set forecast to generating state before request
		if (this.forecast) {
			this.forecast.statusObject = {
				timestamp: new Date().getTime(),
				type: ForecastStatusType.SNAPSHOT_GENERATING,
			};
		}

		this.executeAsyncForecastRequest({
			method: 'PUT',
			endpoint: `/forecasts/${forecastId}/snapshot`,
			payload: forecastPayload,
			navigationAwayErrorMessage: 'Forecast snapshot generation failed',
			errorStatusType: ForecastStatusType.SNAPSHOT_GENERATION_FAILED,
			errorMessage: 'Forecast snapshot generation failed',
			onSuccess: (eventData) => {
				if (eventData.updateData?.lastSnapshotId) {
					const newSnapshotId = eventData.updateData.lastSnapshotId;

					const snapshotsRefresh =
						originalForecast && !originalForecast.isNew
							? this.loadSnapshots(originalForecast)
							: Promise.resolve();

					void snapshotsRefresh.then(() => {
						this.currentShownSnapshotId = newSnapshotId;
					});

					this.requestForecastSnapshotData(newSnapshotId);
					return false; // requestForecastSnapshotData will handle loading state
				} else {
					// success but no snapshot ID - unexpected error
					this.mainForecastState.isLoading = false;
					return true; // we handled loading state
				}
			},
		});
	}

	private loadSnapshots(originalModel: Forecast): Promise<void> {
		if (this.isLoadingSnapshots || !originalModel.id) {
			return Promise.resolve();
		}

		this.isLoadingSnapshots = true;

		return this.requestService
			.api<APIData.Forecast.SnapshotsResponse>(
				'GET',
				`/forecasts/${originalModel.id}/snapshots`,
			)
			.then((response) => {
				this.snapshots = response.snapshots || [];
			})
			.catch((_error) => {
				this.snapshots = [];
			})
			.finally(() => {
				this.isLoadingSnapshots = false;
			});
	}

	// extract regressor data from async forecast response and update regressor instances
	private updateRegressorsFromResponse(
		previewData: APIData.Forecast.NormalizedForecastEventData['previewData'],
	): void {
		if (!this.forecast?.regressors) {
			return;
		}

		// map response regressors to existing forecast regressors by matching metric setup
		const responseRegressors = previewData?.regressorPreviewData || [];

		this.forecast.regressors.forEach((regressor) => {
			const matchingResponse = responseRegressors.find(
				(respRegressor) =>
					respRegressor.regressor.metricSetup.key === regressor.metricSetupKey,
			);

			if (matchingResponse) {
				const regressorData = matchingResponse.regressor;

				regressor.setStatusFromResponse(regressorData.statusObject || null);

				// only update preview data if no error status
				if (!regressor.hasStatusError) {
					regressor.previewData = {
						dataSelects: matchingResponse.dataSelects,
						sample: matchingResponse.sample,
						monthsOfUsedHistoricalData: matchingResponse.monthsOfUsedHistoricalData,
					};
					regressor.initializeTableBlock();
					regressor.clearStatus();
				}
			} else {
				// no preview data for this regressor (likely disabled)
				// clear the loading/generating status
				regressor.clearStatus();
			}
		});
	}

	private loadPendingForecastFromQueryParams(args: Args): boolean {
		// TODO @marcel used to fallbakc to routerService -> check if ok
		const requestId = args.initialTabState?.requestId;

		if (!requestId) {
			return false;
		}

		// get the specific pending forecast that user clicked on
		const pendingEventData = this.mmForecastsService.getPendingForecast(requestId);

		if (!pendingEventData) {
			return false;
		}

		if (!args.model) {
			// unsaved forecast: reconstruct from pending event data
			this.reconstructForecastFromEventData(pendingEventData);
			this.reportForecastStateToParent();
			this.reportSharedStateToParent();
			return true; // skip normal initialization
		}

		// for saved forecasts, the store record already has the updated lastSnapshotId
		// from the pushPayload in socket.ts, so no need to handle it separately
		return false; // continue with normal initialization
	}

	// reconstruct forecast from event data (when user clicks notification after navigating away)
	private reconstructForecastFromEventData(
		eventData: APIData.Forecast.NormalizedForecastEventData,
	): void {
		// update events (recalculate on a saved forecast) have no preview data
		// reuse initializeFromSavedForecast which loads the new latest snapshot
		if (eventData.origin === ForecastPusherEvents.FORECAST_UPDATE_DATA_COMPLETE) {
			const forecastId = eventData.forecast.id;
			if (forecastId !== undefined && forecastId !== null) {
				const savedForecast = this.mmForecastsService.forecastModels.find(
					(f) => f.id === String(forecastId),
				);
				if (savedForecast) {
					this.initializeFromSavedForecast(savedForecast);
					return;
				}
			}
		}

		const forecastConfig = eventData.forecast.config;

		// create unsaved forecast from the stored configuration
		const forecastRecord = this.mmForecastsService.createNewRecord({
			granulation: forecastConfig.granulation,
			comparisonPeriod: forecastConfig.comparisonPeriod,
			metricSetupKey: forecastConfig.metricSetup.key || '',
			metricSetupSpaceAccessId: forecastConfig.metricSetup.spaceAccessId,
			metricSetupDimensions: forecastConfig.metricSetup.dimensions,
			metricSetupFilters: forecastConfig.metricSetup.filters,
			timeframe: forecastConfig.timeframe,
			methodology: forecastConfig.methodology,
			seasonality: forecastConfig.seasonality || [],
			holidaysCountry: forecastConfig.holidaysCountry,
		});

		this.forecast = this.mmForecastsService.createModelFromRecord(forecastRecord);
		this.originalForecastWasSaved = false;
		this.originalForecast = null;
		// set forecast status object if present in stored event data
		if (eventData.forecast.config.statusObject) {
			this.forecast.statusObject = eventData.forecast.config.statusObject;
			const errorMessage = this.forecast.statusObjectMessage || '';
			if (this.forecast.hasStatusError) {
				this.mainForecastState.error = new Error(errorMessage);
			}
		}

		// reconstruct regressors from payload
		if (forecastConfig.regressors && forecastConfig.regressors.length > 0) {
			this.forecast.regressors = forecastConfig.regressors.map(
				(regressorPayload) =>
					new RegressorMetric({
						id: regressorPayload.id ? String(regressorPayload.id) : null,
						forecastId: this.forecast!.id || '',
						metricSetupKey: regressorPayload.metricSetup?.key || '',
						metricSetupSpaceAccessId:
							regressorPayload.metricSetup?.spaceAccessId ?? null,
						metricSetupDimensions: regressorPayload.metricSetup?.dimensions || [],
						metricSetupFilters:
							normalizeFilters(regressorPayload.metricSetup?.filters) || null,
						correlation: regressorPayload.correlation,
						methodology: regressorPayload.methodology,
						enabled:
							regressorPayload.enabled !== undefined
								? regressorPayload.enabled
								: true,
						statusObject: regressorPayload.statusObject || null,
					}),
			);
		}

		// initialize metric setup from forecast
		const metricSetup = new MetricSetup({
			key: forecastConfig.metricSetup.key || '',
			spaceAccessId: forecastConfig.metricSetup.spaceAccessId,
			dimensions: forecastConfig.metricSetup.dimensions,
			filters: forecastConfig.metricSetup.filters,
		});

		this.applyMetricSetup(metricSetup);

		// set the preview data from the event (only present for preview events)
		if (eventData.previewData) {
			this.previewData = {
				dataSelects: eventData.previewData.dataSelects,
				sample: eventData.previewData.sample,
				monthsOfUsedHistoricalData: eventData.previewData.monthsOfUsedHistoricalData,
			};
			this.confidenceScore = eventData.previewData.confidenceScore;
			this.allowedSeasonality =
				eventData.previewData.allowedSeasonality || DEFAULT_ALLOWED_SEASONALITY;

			next(() => {
				this.updateRegressorsFromResponse(eventData.previewData);
			});
		}

		this.mainForecastState.isLoading = false;
		this.mainForecastState.error = undefined;
	}

	private showLoadingInfoLayer(): void {
		this.clearLoadingInfoLayer();

		this.loadingInfoLayerTimeoutId = window.setTimeout(() => {
			if (!this.metricDetailsService.isActivatedRouteExplorer) {
				return;
			}

			const forecastTitle = this.forecast?.title;
			const titleText = forecastTitle ? ` for ${forecastTitle}` : '';
			const message = `Your forecast${titleText} is being prepared. We will notify you once it is ready.`;

			const infoLayer = this.infolayerService.loading(message);
			infoLayer.expire(EXPIRE_INFO);
			this.loadingInfoLayerItem = infoLayer;
		}, 10000);
	}

	private clearLoadingInfoLayer(): void {
		if (this.loadingInfoLayerTimeoutId) {
			clearTimeout(this.loadingInfoLayerTimeoutId);
			this.loadingInfoLayerTimeoutId = null;
		}

		if (this.loadingInfoLayerItem) {
			if (this.loadingInfoLayerItem.timeout) {
				clearTimeout(this.loadingInfoLayerItem.timeout);
				this.loadingInfoLayerItem.timeout = null;
			}
			this.loadingInfoLayerItem.close();
			this.loadingInfoLayerItem = null;
		}
	}

	private reportMetricSelectorStateToParent(): void {
		this.args.onDisabledTooltipTextChange?.(
			this.originalForecastWasSaved
				? 'Saved forecasts keep their original forecasting period and graph by settings. Create a new forecast to change them.'
				: undefined,
		);
	}

	private reportForecastStateToParent(): void {
		if (this.forecast && this.args.onTabStateChange) {
			this.args.onTabStateChange({
				methodology: this.forecast.methodology,
				seasonality: this.forecast.seasonality,
				timeframe: this.forecast.timeframe,
				endDate: this.forecast.endDate,
				granulation: this.forecast.granulation,
				comparisonPeriod: this.forecast.comparisonPeriod,
				holidaysCountry: this.forecast.holidaysCountry,
				forecastId: this.originalForecast?.id ?? null,
				requestId: this.latestMainForecastRequestId ?? null,
			});
		}

		this.reportMetricSelectorStateToParent();
	}

	private applyMetricSetup(metricSetup: MetricSetup): void {
		this.setupMetricSetupOnChange(metricSetup);

		this.metricSetup = metricSetup;

		const metricData = metricSetup.metricData;
		this.limitations = new MetricLimitations({
			metrics: metricData ? [metricData] : [],
		});
	}

	private setupMetricSetupOnChange(metricSetup: MetricSetup): void {
		metricSetup.onChange = (prop, _value) => {
			switch (prop) {
				case 'key': {
					const limitations = this.limitations;
					limitations.changeMetric(metricSetup.metricData);
					void limitations.limitationsLoaded?.then(() => {
						this.setOptimalUnit();
						this.requestForecastDataOnce();
					});

					this.reportSharedStateToParent();

					if (this.forecast) {
						this.mmForecastsService.trackForecastMetricEvent(
							TrackingEvent.FORECASTING_METRIC_SELECTED,
							this.forecast,
							'forecasting',
						);
					}
					break;
				}
				case 'dimensions':
				case 'filters': {
					this.reportForecastStateToParent();
					this.requestForecastDataOnce();
					break;
				}
			}
		};
	}

	private reportSharedStateToParent(): void {
		if (this.args.onSharedStateChange && this.metricSetup?.metricSetupData) {
			this.args.onSharedStateChange({ metricSetup: this.metricSetup.metricSetupData });
		}
	}
	<template>
		<div
			class="metric-details__content metric-details__forecasts"
			{{toggleScrolledClass}}
			{{didUpdate this.onParentMetricChange @sharedState}}
		>
			<div class="metric-details__header-row">
				{{yield to="header"}}
				<div class="metric-details__header-actions">
					<ForecastsHeaderActionsComponent
						@forecast={{this.forecast}}
						@originalForecast={{this.originalForecast}}
						@isForecastSaved={{this.isForecastSaved}}
						@isRecalculateDisabled={{this.isRecalculateDisabled}}
						@isLoading={{this.isAddOrRemoveInProgress}}
						@isError={{this.isError}}
						@onSave={{this.addToMyForecasts}}
						@onRecalculate={{this.recalculateForecast}}
						@onDuplicate={{this.duplicateForecast}}
						@onDelete={{this.deleteForecast}}
						@onShare={{@onShare}}
						@onClose={{@onClose}}
					/>
				</div>
			</div>

			<div class="module-box {{unless this.forecast 'module-box--empty'}}">
				{{#if this.forecast}}
					<div class="metric-details__topbar">
						<MetricDetailsForecastsTopbarComponent
							@forecast={{this.forecast}}
							@snapshots={{this.snapshots}}
							@currentSnapshotId={{this.currentShownSnapshotId}}
							@isSetupLocked={{this.areSettingsDisabled}}
							@isForecastSaved={{this.isForecastSaved}}
							@isRecalculateDisabled={{this.isRecalculateDisabled}}
							@onSettingsChange={{this.changeForecastSettings}}
							@onSnapshotChange={{this.selectSnapshot}}
							@onRecalculate={{this.recalculateForecast}}
						/>
					</div>

					<div class="status status--loaded">
						<ForecastsDataComponent
							@forecast={{this.forecast}}
							@previewData={{this.previewData}}
							@mainForecastRequestState={{this.mainForecastState}}
							@isInitialLoading={{this.isInitialLoading}}
							@isRefreshingWithData={{this.isRefreshingWithData}}
							@loadingMessages={{this.loadingMessages}}
							@loadingMessageIntervalTime={{this.loadingMessageIntervalTime}}
							@onRegressorChange={{this.handleRegressorChange}}
							@onRequestForecastPreview={{this.handleRequestForecastPreview}}
							@executeErrorButtonAction={{this.executeErrorButtonAction}}
							@currentShownSnapshotId={{this.currentShownSnapshotId}}
							@isViewingHistoricalSnapshot={{this.isViewingHistoricalSnapshot}}
						>
							<:settings as |settingsBlock|>
								{{#if this.forecast}}
									<MetricDetailsSettingsTabsComponent
										@tabs={{this.settingsTabs}}
										as |tab|
									>
										{{#if tab.isVisualize}}
											<MetricDetailsSettingsVisualizeComponent
												@metricSetup={{this.metricSetup}}
												@blockM={{settingsBlock}}
												@isDisabled={{this.areSettingsDisabled}}
												@origin="forecast"
											/>
										{{else if tab.isMethodology}}
											<MetricDetailsSettingsForecastMethodologyComponent
												@forecast={{this.forecast}}
												@isDisabled={{this.areSettingsDisabled}}
												@allowedSeasonality={{this.allowedSeasonality}}
												@onChangeMethodology={{this.changeMethodology}}
												@onChangeParameters={{this.changeParameters}}
											/>
										{{/if}}
									</MetricDetailsSettingsTabsComponent>
								{{/if}}
							</:settings>
						</ForecastsDataComponent>
					</div>
				{{/if}}
			</div>

			{{#if this.showConfidenceScore}}
				{{#if this.confidenceScore}}
					<ForecastsDataConfidenceScoreComponent @model={{this.confidenceScore}} />
				{{/if}}
			{{/if}}

			{{#if this.metricSetup}}
				<MetricDetailsRelatedForecastsComponent
					@metricSetup={{this.metricSetup}}
					@excludeForecastId={{this.originalForecast.id}}
					@title={{this.relatedForecastsTitle}}
					@hideEmpty={{true}}
				/>
			{{/if}}
		</div>
	</template>
}
